import React, { useEffect, useMemo, useState, Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, AlertTriangle, Download, TrendingUp, Sparkles, Calendar, Trash2, Clock, Plus, User, Target, Search, ChevronLeft, ChevronRight, Archive } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { getAssessmentHistory } from '../../../assessment/utils/assessmentStorage';
import type { AssessmentHistoryEntry } from '../../../assessment';
import { TeacherV2Route } from '../TeacherSidebarV2';
import { DjayAvatar } from '../../../mascot';
import { CreateAssessmentPanel } from '../components/CreateAssessmentPanel';
import { dataLayer } from '../../../data/dataLayer';
import { DOMAIN_META } from '../../../assessment/utils/assessmentDomainConfig';
import type { Account } from '../../../accounts';
interface AssessmentsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onNavigate?: (route: TeacherV2Route) => void;
  activeAccount?: Account;
  selectedClassroomId?: string | null;
  initialTab?: 'klaarzetten' | 'resultaten';
  initialSearchQuery?: string;
}
type MainTabType = 'klaarzetten' | 'resultaten';
type SubTabType = 'all' | 'niveaubepaling' | 'toegewezen';
const ITEMS_PER_PAGE = 20;
export function AssessmentsPage({
  students,
  selectedGroup,
  activeAccount,
  selectedClassroomId,
  initialTab,
  initialSearchQuery
}: AssessmentsPageProps) {
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [mainTab, setMainTab] = useState<MainTabType>(initialTab || 'klaarzetten');
  const [activeSubTab, setActiveSubTab] = useState<SubTabType>('all');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  // Apply initial values when they change
  useEffect(() => {
    if (initialTab) setMainTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    if (initialSearchQuery !== undefined) setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);
  const loadData = async () => {
    const [history, assignmentsData] = await Promise.all([getAssessmentHistory(selectedGroup), dataLayer.getTeacherAssignments()]);
    setAssessmentHistory(history);
    // Filter assignments for this classroom/group
    const allAssignments = Object.values(assignmentsData);
    const relevantAssignments = allAssignments.filter((a: any) => (a.classroomId || null) === (selectedClassroomId || null) || !a.classroomId && a.group === selectedGroup);
    setAssignments(relevantAssignments.sort((a: any, b: any) => b.createdAt - a.createdAt));
  };
  useEffect(() => {
    loadData();
  }, [selectedGroup, selectedClassroomId]);
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSubTab, searchQuery, selectedDomain, selectedPeriod]);
  const niveaubepalingen = assessmentHistory.filter(a => !a.assignmentId);
  const toegewezenToetsen = assessmentHistory.filter(a => !!a.assignmentId);
  const filteredHistory = useMemo(() => {
    let filtered = assessmentHistory;
    // 1. Sub-tab filter
    if (activeSubTab === 'niveaubepaling') {
      filtered = niveaubepalingen;
    } else if (activeSubTab === 'toegewezen') {
      filtered = toegewezenToetsen;
    }
    // 2. Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        const student = students.find(s => s.id === entry.learnerId);
        const studentName = student ? student.name.toLowerCase() : 'onbekend';
        return studentName.includes(query);
      });
    }
    // 3. Domain filter
    if (selectedDomain !== 'all') {
      filtered = filtered.filter(entry => entry.domainId === selectedDomain);
    }
    // 4. Period filter
    if (selectedPeriod !== 'all') {
      const now = Date.now();
      const dayMs = 24 * 60 * 60 * 1000;
      let periodMs = 0;
      if (selectedPeriod === 'week') periodMs = 7 * dayMs;
      if (selectedPeriod === 'month') periodMs = 30 * dayMs;
      if (selectedPeriod === '3months') periodMs = 90 * dayMs;
      filtered = filtered.filter(entry => now - entry.completedAt <= periodMs);
    }
    return filtered;
  }, [assessmentHistory, activeSubTab, searchQuery, selectedDomain, selectedPeriod, students, niveaubepalingen, toegewezenToetsen]);
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const paginatedHistory = filteredHistory.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const avgAssessmentScore = assessmentHistory.length > 0 ? Math.round(assessmentHistory.reduce((sum, a) => sum + a.score, 0) / assessmentHistory.length) : 0;
  const handleExportAssessmentCsv = () => {
    const headers = ['Datum', 'Leerling', 'Type', 'Domein', 'Score (%)', 'Aantal Vragen', 'Tijd (min)'];
    const rows = filteredHistory.map(a => {
      const dateStr = new Date(a.completedAt).toLocaleDateString('nl-NL');
      const student = students.find(s => s.id === a.learnerId);
      const studentName = student ? student.name : 'Onbekend';
      const typeLabel = a.assignmentId ? 'Toegewezen toets' : 'Niveaubepaling';
      const domainLabel = a.domainId ? DOMAIN_META[a.domainId]?.label : 'Onbekend';
      return [dateStr, `"${studentName}"`, `"${typeLabel}"`, `"${domainLabel}"`, a.score, a.totalQuestions, Math.round(a.totalTimeSeconds / 60)].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `toetsresultaten_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDeleteAssignment = async (id: string) => {
    if (window.confirm('Weet je zeker dat je deze toets wilt verwijderen?')) {
      await dataLayer.deleteTeacherAssignment(id);
      loadData();
    }
  };
  const handleArchiveAssignment = async (id: string) => {
    if (window.confirm('Weet je zeker dat je deze toets wilt archiveren?')) {
      await dataLayer.archiveTeacherAssignment(id);
      loadData();
    }
  };
  const domainOptions = [{
    value: 'all',
    label: 'Alle domeinen'
  }, ...Object.entries(DOMAIN_META).map(([id, meta]) => ({
    value: id,
    label: meta.label
  }))];
  const periodOptions = [{
    value: 'all',
    label: 'Alle periodes'
  }, {
    value: 'week',
    label: 'Afgelopen week'
  }, {
    value: 'month',
    label: 'Afgelopen maand'
  }, {
    value: '3months',
    label: 'Afgelopen 3 maanden'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  const tableRowVariants = {
    hidden: {
      opacity: 0,
      x: -10
    },
    show: {
      opacity: 1,
      x: 0
    }
  };
  return <PageLayout title="Toetsen" description="Toetsresultaten en niveaubepaling" data-id="element-5554">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="show" data-id="element-5555">
        {/* Page-level Tab Switcher */}
        <motion.div variants={itemVariants} className="flex justify-center sm:justify-start" data-id="element-5556">
          <div className="bg-slate-100 p-1 rounded-xl inline-flex" data-id="element-5557">
            <button onClick={() => setMainTab('klaarzetten')} className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${mainTab === 'klaarzetten' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5558">
              Klaarzetten
            </button>
            <button onClick={() => setMainTab('resultaten')} className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${mainTab === 'resultaten' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5559">
              Resultaten
            </button>
          </div>
        </motion.div>

        {mainTab === 'klaarzetten' && <motion.div key="klaarzetten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-5560">
            {/* Toets klaarzetten CTA */}
            <Card className="relative p-6 border-orange-200 shadow-sm bg-gradient-to-br from-orange-50/50 to-orange-100/30 flex flex-col sm:flex-row items-center sm:items-start gap-6 overflow-hidden group hover:shadow-md transition-shadow" data-id="element-5561">
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-[shimmer_2s_infinite]" data-id="element-5562" />
              <div className="relative w-16 h-16 bg-white text-orange-600 rounded-2xl flex items-center justify-center flex-shrink-0 border border-orange-200 shadow-sm" data-id="element-5563">
                <motion.div animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut'
            }} data-id="element-5564">
                  <Sparkles className="w-8 h-8 text-orange-500" data-id="element-5565" />
                </motion.div>
              </div>
              <div className="flex-1 text-center sm:text-left relative z-10" data-id="element-5566">
                <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-5567">
                  Toets klaarzetten
                </h2>
                <p className="text-slate-600 mb-4 max-w-2xl leading-relaxed" data-id="element-5568">
                  Zet een toets klaar voor je klas of voor specifieke
                  leerlingen. Kies het vakgebied en stel eventueel een deadline
                  in.
                </p>
                <Button onClick={() => setIsCreatePanelOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm" data-id="element-5569">
                  <Plus className="w-4 h-4 mr-2" data-id="element-5570" />
                  Nieuwe toets klaarzetten
                </Button>
              </div>
            </Card>

            {/* Active Assignments Section */}
            {assignments.length > 0 ? <div className="space-y-4" data-id="element-5571">
                <h2 className="text-xl font-black text-slate-900" data-id="element-5572">
                  Klaargezette Toetsen
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5573">
                  {assignments.filter((a: any) => a.status !== 'archived').map(assignment => {
              const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
              const targetCount = assignment.wholeClass ? students.length : assignment.targetStudentIds.length;
              // Count completions (matching domain and completed after assignment creation)
              const completions = assessmentHistory.filter(a => a.domainId === assignment.domainId && a.completedAt > assignment.createdAt).length;
              const completedCount = Math.min(completions, targetCount);
              const progressPercent = targetCount > 0 ? Math.round(completedCount / targetCount * 100) : 0;
              let status = assignment.status;
              if (assignment.deadline && Date.now() > assignment.deadline) status = 'expired';
              if (completedCount >= targetCount && targetCount > 0) status = 'completed';
              const Icon = meta?.icon;
              return <Card key={assignment.id} className="p-4 border-slate-200 flex flex-col" data-id="element-5574">
                          <div className="flex items-start justify-between mb-3" data-id="element-5575">
                            <div className="flex items-center gap-3" data-id="element-5576">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-5577">
                                {Icon ? <Icon className="w-4 h-4" data-id="element-5578" /> : '📝'}
                              </div>
                              <div data-id="element-5579">
                                <div className="flex items-center gap-2" data-id="element-5580">
                                  <h3 className="font-bold text-sm text-slate-900" data-id="element-5581">
                                    {assignment.domainLabel}
                                  </h3>
                                  {assignment.type === 'general' && <Badge variant="default" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] py-0 px-1.5" data-id="element-5582">
                                      Algemeen
                                    </Badge>}
                                  {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] py-0 px-1.5" data-id="element-5583">
                                      Gericht
                                    </Badge>}
                                </div>
                                {assignment.type === 'targeted' && assignment.selectedSkillSummary && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1" data-id="element-5584">
                                      {assignment.selectedSkillSummary}
                                    </div>}
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-1" data-id="element-5585">
                                  <Calendar className="w-3 h-3" data-id="element-5586" />
                                  {new Date(assignment.createdAt).toLocaleDateString('nl-NL')}
                                  {assignment.deadline && <>
                                      <span data-id="element-5587">•</span>
                                      <span className="text-orange-600" data-id="element-5588">
                                        Deadline:{' '}
                                        {new Date(assignment.deadline).toLocaleDateString('nl-NL')}
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-5589">
                              <button onClick={() => handleArchiveAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors" aria-label="Archiveren" title="Archiveren" data-id="element-5590">
                                <Archive className="w-4 h-4" data-id="element-5591" />
                              </button>
                              <button onClick={() => handleDeleteAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors" aria-label="Verwijderen" data-id="element-5592">
                                <Trash2 className="w-4 h-4" data-id="element-5593" />
                              </button>
                            </div>
                          </div>

                          <div className="mt-auto space-y-2" data-id="element-5594">
                            <div className="flex items-center justify-between text-xs" data-id="element-5595">
                              <span className="text-slate-600 font-medium" data-id="element-5596">
                                {completedCount} van {targetCount} voltooid
                              </span>
                              {status === 'active' && <Badge variant="default" className="bg-indigo-100 text-indigo-700 text-[10px] py-0 px-1.5" data-id="element-5597">
                                  Actief
                                </Badge>}
                              {status === 'completed' && <Badge variant="success" className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5" data-id="element-5598">
                                  Voltooid
                                </Badge>}
                              {status === 'expired' && <Badge variant="danger" className="bg-rose-100 text-rose-700 text-[10px] py-0 px-1.5" data-id="element-5599">
                                  Verlopen
                                </Badge>}
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-5600">
                              <div className={`h-full rounded-full transition-all duration-1000 ${status === 'completed' ? 'bg-emerald-500' : status === 'expired' ? 'bg-rose-500' : 'bg-indigo-500'}`} style={{
                      width: `${progressPercent}%`
                    }} data-id="element-5601" />
                            </div>
                          </div>
                        </Card>;
            })}
                </div>
              </div> : <div className="text-center py-16 bg-slate-50/50 rounded-2xl border border-slate-100 flex flex-col items-center justify-center" data-id="element-5602">
                <div className="mb-4" data-id="element-5603">
                  <DjayAvatar size="md" emotion="happy" data-id="element-5604" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-2" data-id="element-5605">
                  Geen actieve toetsen
                </h3>
                <p className="text-sm text-slate-500 max-w-sm" data-id="element-5606">
                  Je hebt op dit moment geen toetsen klaargezet voor je
                  leerlingen.
                </p>
              </div>}
          </motion.div>}

        {mainTab === 'resultaten' && <motion.div key="resultaten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-5607">
            {/* Summary Strip */}
            <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-5608">
              <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-5609">
                <div className="flex-1 p-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-5610">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0 shadow-inner" data-id="element-5611">
                    <ClipboardCheck className="w-5 h-5" data-id="element-5612" />
                  </div>
                  <div data-id="element-5613">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5614">
                      Toetsen Totaal
                    </div>
                    <div className="text-2xl font-black text-slate-900" data-id="element-5615">
                      {assessmentHistory.length}
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-5616">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 shadow-inner" data-id="element-5617">
                    <Target className="w-5 h-5" data-id="element-5618" />
                  </div>
                  <div data-id="element-5619">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5620">
                      Niveaubepalingen
                    </div>
                    <div className="text-2xl font-black text-slate-900" data-id="element-5621">
                      {niveaubepalingen.length}
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-5622">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 shadow-inner" data-id="element-5623">
                    <TrendingUp className="w-5 h-5" data-id="element-5624" />
                  </div>
                  <div className="flex-1" data-id="element-5625">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5626">
                      Gemiddelde Score
                    </div>
                    <div className="flex items-center gap-3" data-id="element-5627">
                      <div className="text-2xl font-black text-slate-900" data-id="element-5628">
                        {avgAssessmentScore}%
                      </div>
                      {assessmentHistory.length > 0 && <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[100px] hidden lg:block" data-id="element-5629">
                          <motion.div className={`h-full rounded-full ${avgAssessmentScore >= 70 ? 'bg-emerald-500' : avgAssessmentScore >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} initial={{
                      width: 0
                    }} animate={{
                      width: `${avgAssessmentScore}%`
                    }} transition={{
                      duration: 1,
                      delay: 0.5,
                      ease: 'easeOut'
                    }} data-id="element-5630" />
                        </div>}
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-5631">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0 shadow-inner" data-id="element-5632">
                    <Clock className="w-5 h-5" data-id="element-5633" />
                  </div>
                  <div className="min-w-0" data-id="element-5634">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5635">
                      Klaarstaan
                    </div>
                    <div className="text-2xl font-black text-slate-900" data-id="element-5636">
                      {assignments.filter((a: any) => a.status === 'active' || !a.status).length}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Toetsresultaten Section */}
            <Card className="p-5 border-slate-200 shadow-sm bg-white flex flex-col" data-id="element-5637">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6" data-id="element-5638">
                <div data-id="element-5639">
                  <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-5640">
                    Toetsresultaten Historie
                  </h2>
                  <p className="text-slate-500" data-id="element-5641">
                    Overzicht van alle gemaakte toetsen in deze groep.
                  </p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-2 max-w-xs" data-id="element-5642">
                  <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-5643" />
                  <div className="text-xs text-orange-800" data-id="element-5644">
                    <strong data-id="element-5645">Niveaubepalingen</strong> zijn indicatief en geen
                    officiële Cito-toetsen.
                  </div>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6 bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-5646">
                <div className="w-full sm:w-1/3" data-id="element-5647">
                  <Input placeholder="Zoek leerling..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-5649" />} size="sm" data-id="element-5648" />
                </div>
                <div className="w-full sm:w-1/3" data-id="element-5650">
                  <Select options={domainOptions} value={selectedDomain} onChange={e => setSelectedDomain(e.target.value)} size="sm" data-id="element-5651" />
                </div>
                <div className="w-full sm:w-1/3" data-id="element-5652">
                  <Select options={periodOptions} value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} size="sm" data-id="element-5653" />
                </div>
              </div>

              {/* Sub-Tabs */}
              <div className="flex items-center gap-6 border-b border-slate-200 mb-6 overflow-x-auto no-scrollbar pb-px" data-id="element-5654">
                <button onClick={() => setActiveSubTab('all')} className={`pb-3 text-sm font-bold whitespace-nowrap transition-colors relative ${activeSubTab === 'all' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5655">
                  Alle Toetsen
                  <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-5656">
                    ({assessmentHistory.length})
                  </span>
                  {activeSubTab === 'all' && <motion.div layoutId="activeSubTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" data-id="element-5657" />}
                </button>
                <button onClick={() => setActiveSubTab('toegewezen')} className={`pb-3 text-sm font-bold whitespace-nowrap transition-colors relative ${activeSubTab === 'toegewezen' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5658">
                  Toegewezen Toetsen
                  <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-5659">
                    ({toegewezenToetsen.length})
                  </span>
                  {activeSubTab === 'toegewezen' && <motion.div layoutId="activeSubTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" data-id="element-5660" />}
                </button>
                <button onClick={() => setActiveSubTab('niveaubepaling')} className={`pb-3 text-sm font-bold whitespace-nowrap transition-colors relative ${activeSubTab === 'niveaubepaling' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5661">
                  Niveaubepalingen
                  <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-5662">
                    ({niveaubepalingen.length})
                  </span>
                  {activeSubTab === 'niveaubepaling' && <motion.div layoutId="activeSubTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" data-id="element-5663" />}
                </button>
              </div>

              {paginatedHistory.length > 0 ? <>
                  <div className="overflow-x-auto mb-6" data-id="element-5664">
                    <table className="w-full text-left border-collapse min-w-[600px]" data-id="element-5665">
                      <thead data-id="element-5666">
                        <tr className="border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5667">
                          <th className="py-3 px-4" data-id="element-5668">Datum</th>
                          <th className="py-3 px-4" data-id="element-5669">Leerling</th>
                          <th className="py-3 px-4" data-id="element-5670">Type</th>
                          <th className="py-3 px-4" data-id="element-5671">Domein</th>
                          <th className="py-3 px-4" data-id="element-5672">Score</th>
                          <th className="py-3 px-4" data-id="element-5673">Vragen</th>
                          <th className="py-3 px-4" data-id="element-5674">Tijd</th>
                        </tr>
                      </thead>
                      <motion.tbody className="divide-y divide-slate-100" variants={containerVariants} initial="hidden" animate="show" data-id="element-5675">
                        {paginatedHistory.map((entry, idx) => {
                    const dateStr = new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    });
                    const isHigh = entry.score >= 70;
                    const isMedium = entry.score >= 50 && entry.score < 70;
                    const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
                    const student = students.find(s => s.id === entry.learnerId);
                    const studentName = student ? student.name : 'Onbekend';
                    return <motion.tr key={idx} custom={idx} variants={tableRowVariants} className="hover:bg-slate-50/80 transition-colors group" data-id="element-5676">
                              <td className="py-3 px-4 text-sm font-medium text-slate-900 whitespace-nowrap" data-id="element-5677">
                                {dateStr}
                              </td>
                              <td className="py-3 px-4 text-sm" data-id="element-5678">
                                {student ? <div className="flex items-center gap-2" data-id="element-5679">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0" data-id="element-5680">
                                      <User className="w-3 h-3" data-id="element-5681" />
                                    </div>
                                    <span className="font-medium text-slate-900" data-id="element-5682">
                                      {studentName}
                                    </span>
                                  </div> : <span className="text-slate-400 italic flex items-center gap-2" data-id="element-5683">
                                    <User className="w-4 h-4" data-id="element-5684" />
                                    Onbekend
                                  </span>}
                              </td>
                              <td className="py-3 px-4" data-id="element-5685">
                                {entry.assignmentId ? <Badge variant="warning" className="bg-orange-50 text-orange-700 border-orange-200" data-id="element-5686">
                                    Toegewezen
                                  </Badge> : <Badge variant="info" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-5687">
                                    Niveaubepaling
                                  </Badge>}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600" data-id="element-5688">
                                {domainMeta ? <div className="flex items-center gap-2" data-id="element-5689">
                                    <domainMeta.icon className={`w-4 h-4 ${domainMeta.color}`} data-id="element-5690" />
                                    <span data-id="element-5691">{domainMeta.label}</span>
                                  </div> : <span className="text-slate-400 italic" data-id="element-5692">
                                    Algemeen
                                  </span>}
                              </td>
                              <td className="py-3 px-4" data-id="element-5693">
                                <div className="flex items-center gap-3" data-id="element-5694">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold w-12 justify-center ${isHigh ? 'bg-emerald-100 text-emerald-700' : isMedium ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-5695">
                                    {entry.score}%
                                  </span>
                                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block" data-id="element-5696">
                                    <motion.div className={`h-full rounded-full ${isHigh ? 'bg-emerald-500' : isMedium ? 'bg-amber-500' : 'bg-rose-500'}`} initial={{
                              width: 0
                            }} animate={{
                              width: `${entry.score}%`
                            }} transition={{
                              duration: 0.8,
                              delay: 0.05
                            }} data-id="element-5697" />
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600 font-medium" data-id="element-5698">
                                {entry.totalQuestions}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600 font-medium" data-id="element-5699">
                                {Math.round(entry.totalTimeSeconds / 60)} min
                              </td>
                            </motion.tr>;
                  })}
                      </motion.tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && <div className="flex items-center justify-between border-t border-slate-100 pt-4 mb-6" data-id="element-5700">
                      <div className="text-sm text-slate-500" data-id="element-5701">
                        Pagina{' '}
                        <span className="font-bold text-slate-900" data-id="element-5702">
                          {currentPage}
                        </span>{' '}
                        van{' '}
                        <span className="font-bold text-slate-900" data-id="element-5703">
                          {totalPages}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-5704">
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2" data-id="element-5705">
                          <ChevronLeft className="w-4 h-4" data-id="element-5706" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2" data-id="element-5707">
                          <ChevronRight className="w-4 h-4" data-id="element-5708" />
                        </Button>
                      </div>
                    </div>}
                </> : <motion.div className="text-center py-16 bg-slate-50/50 rounded-2xl border border-slate-100 mb-6 flex flex-col items-center justify-center" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.4
          }} data-id="element-5709">
                  <div className="mb-4" data-id="element-5710">
                    <DjayAvatar size="md" emotion="happy" data-id="element-5711" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900 mb-2" data-id="element-5712">
                    Geen resultaten gevonden
                  </h3>
                  <p className="text-sm text-slate-500 max-w-sm" data-id="element-5713">
                    Er zijn geen toetsen die voldoen aan je huidige filters.
                  </p>
                </motion.div>}

              <div className="mt-auto pt-4 border-t border-slate-100" data-id="element-5714">
                <Button onClick={handleExportAssessmentCsv} variant="outline" disabled={filteredHistory.length === 0} className="w-full sm:w-auto justify-start text-left border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300 transition-all" data-id="element-5715">
                  <Download className="w-4 h-4 mr-3 text-orange-500" data-id="element-5716" />
                  <div data-id="element-5717">
                    <div className="font-bold text-slate-900" data-id="element-5718">
                      Toetsresultaten CSV
                    </div>
                    <div className="text-xs text-slate-500 font-normal" data-id="element-5719">
                      Exporteert alle {filteredHistory.length} gefilterde
                      resultaten
                    </div>
                  </div>
                </Button>
              </div>
            </Card>
          </motion.div>}
      </motion.div>

      <CreateAssessmentPanel open={isCreatePanelOpen} onClose={() => setIsCreatePanelOpen(false)} students={students} selectedGroup={selectedGroup} teacherId={activeAccount?.id || 'unknown'} classroomId={selectedClassroomId || null} onSuccess={() => {
      loadData();
    }} data-id="element-5720" />
    </PageLayout>;
}